import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/brand/Wordmark";

const navItems = [
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background/85 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-wordmark"
          className="font-display text-xl md:text-2xl tracking-tight"
        >
          <Wordmark className="h-8 md:h-10" />
        </Link>

        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="text-sm text-foreground/80 hover:text-oxblood transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/login"
            data-testid="header-signin"
            className="text-sm text-foreground/80 hover:text-foreground transition-colors"
          >
            Sign in
          </Link>
          <Button
            asChild
            data-testid="header-cta-trial"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none px-5 h-10"
          >
            <Link to="/signup">Start 14-Day Free Trial</Link>
          </Button>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 -mr-2 text-foreground"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-background border-t border-border"
        >
          <nav
            aria-label="Mobile"
            className="px-6 py-6 flex flex-col gap-5"
          >
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="text-base text-foreground/85"
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              data-testid="mobile-signin"
              className="text-base text-foreground/85"
            >
              Sign in
            </Link>
            <Button
              asChild
              data-testid="mobile-cta-trial"
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11 mt-2"
            >
              <Link to="/signup" onClick={() => setOpen(false)}>
                Start 14-Day Free Trial
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
